import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO/SEO';

const Terms: React.FC = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Terms of Service"
        description="Read the terms of service for ERHR Global HR consulting services. Service agreements, liability policies, and user responsibilities."
        url="https://erhrglobal.com/terms"
        noindex={true}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Terms of Service</h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Please read these terms and conditions carefully before using our services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <div className="mb-8">
              <p className="text-gray-600 mb-4">
                <strong>Effective Date:</strong> January 1, 2025
              </p>
              <p className="text-gray-600 mb-8">
                <strong>Last Updated:</strong> January 1, 2025
              </p>
            </div>

            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-700 mb-4">
                  By accessing and using the services provided by ERHR Global ("we," "our," or "us"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Services Description</h2>
                <p className="text-gray-700 mb-4">
                  ERHR Global provides human resources consulting services, including but not limited to:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>Transitional HR services</li>
                  <li>Strategic HR consulting</li>
                  <li>Performance management solutions</li>
                  <li>HR systems implementation</li>
                  <li>Benefits strategy development</li>
                  <li>Policies and procedures development</li>
                  <li>Employee engagement programs</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Client Responsibilities</h2>
                <p className="text-gray-700 mb-4">
                  Clients agree to:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                  <li>Provide accurate and complete information necessary for service delivery</li>
                  <li>Cooperate with ERHR Global personnel in the performance of services</li>
                  <li>Make timely payments as agreed upon in the service agreement</li>
                  <li>Comply with all applicable laws and regulations</li>
                  <li>Maintain confidentiality of proprietary methodologies and processes</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Confidentiality</h2>
                <p className="text-gray-700 mb-4">
                  ERHR Global acknowledges that it may have access to certain confidential information of the client. We agree to maintain the confidentiality of such information and not to disclose it to third parties without prior written consent, except as required by law.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Intellectual Property</h2>
                <p className="text-gray-700 mb-4">
                  All intellectual property rights in materials, methodologies, and deliverables created by ERHR Global remain the property of ERHR Global, unless otherwise specified in a written agreement. Clients receive a license to use such materials for their internal business purposes.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Limitation of Liability</h2>
                <p className="text-gray-700 mb-4">
                  ERHR Global's liability for any claim arising out of or relating to these terms or the services provided shall not exceed the total amount paid by the client for the specific services giving rise to the claim. In no event shall ERHR Global be liable for indirect, incidental, special, or consequential damages.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Termination</h2>
                <p className="text-gray-700 mb-4">
                  Either party may terminate the service agreement with written notice as specified in the individual service agreement. Upon termination, each party shall return or destroy confidential information of the other party.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Governing Law</h2>
                <p className="text-gray-700 mb-4">
                  These terms shall be governed by and construed in accordance with the laws of the jurisdiction in which ERHR Global operates, without regard to its conflict of law provisions.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Dispute Resolution</h2>
                <p className="text-gray-700 mb-4">
                  Any disputes arising from these terms or the services provided shall first be addressed through good faith negotiations. If unresolved, disputes may be submitted to binding arbitration or mediation as agreed upon by both parties.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Modifications</h2>
                <p className="text-gray-700 mb-4">
                  ERHR Global reserves the right to modify these terms at any time. Changes will be effective immediately upon posting on our website. Continued use of our services after such changes constitutes acceptance of the new terms.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Information</h2>
                <p className="text-gray-700 mb-4">
                  If you have any questions about these Terms of Service, please contact us through our website's contact form or at the contact information provided on our website.
                </p>
              </div>
            </div>

            <div className="mt-12 p-6 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                These terms constitute the entire agreement between you and ERHR Global regarding the use of our services and supersede all prior agreements and understandings, whether written or oral, relating to such subject matter.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
